import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { LOCALE_STORAGE_KEY, toLocalePath, type Locale } from "@/i18n/locale";

type Props = {
  /** Visual style — defaults to dark text on light backgrounds. */
  variant?: "light" | "dark";
};

/**
 * Compact EN / ES toggle. Swaps to the equivalent route in the other
 * locale and persists the choice for return visits.
 */
export function LocaleSwitcher({ variant = "light" }: Props) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current: Locale =
    pathname === "/es" || pathname.startsWith("/es/") ? "es" : "en";

  // On first mount, honor saved preference IF the user landed on the
  // "other" locale's bare home. Avoids hijacking deep links.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {}
    if (!saved) return;
    if (saved === current) return;
    if (pathname !== "/" && pathname !== "/es") return;
    const target = toLocalePath(pathname, saved as Locale);
    if (target !== pathname) {
      navigate({ to: target, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = (target: Locale) => {
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, target);
    } catch {}
    if (target === current) return;
    navigate({ to: toLocalePath(pathname, target) });
  };

  const baseColor = variant === "dark" ? "#FFFFFF" : "#4B1F4F";
  const dimColor = variant === "dark" ? "rgba(255,255,255,0.55)" : "#9B7B9F";

  const btn = (label: string, code: Locale, active: boolean) => (
    <button
      key={code}
      type="button"
      aria-label={`Switch to ${code === "es" ? "Spanish" : "English"}`}
      aria-pressed={active}
      onClick={() => onClick(code)}
      style={{
        background: "none",
        border: "none",
        padding: "4px 6px",
        cursor: "pointer",
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 12,
        fontWeight: active ? 600 : 400,
        color: active ? baseColor : dimColor,
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        border: `1px solid ${variant === "dark" ? "rgba(255,255,255,0.25)" : "#E8D5DA"}`,
        borderRadius: 999,
        padding: "2px 4px",
      }}
    >
      {btn("EN", "en", current === "en")}
      <span style={{ color: dimColor, fontSize: 10 }}>·</span>
      {btn("ES", "es", current === "es")}
    </div>
  );
}
